import { EmptyState } from '@/components/ui/EmptyState';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorState } from '@/components/ui/ErrorState';
import { useFeaturedEvent } from '../hooks/useFeaturedEvent';
import { usePublicEvents } from '../hooks/usePublicEvents';
import { EVENTS_STRINGS } from '../constants/events-strings';
import { EventCard } from './EventCard';
import type { PublicEvent } from '../types';

export interface PublicEventsSectionProps {
  /**
   * CMS-driven heading (MYN-197/MYN-198's `events-teaser` section content).
   * Defaults to the pre-CMS hardcoded copy so any other/older caller is
   * unaffected.
   */
  eyebrow?: string;
  title?: string;
  intro?: string;
}

/**
 * The public events section (FR-34), rendered by BOTH Home and Benefits. One
 * component, two consumers - a second copy would be the duplication failure the
 * standards treat as a MAJOR.
 *
 * There is no standalone public events page, and there is no full-width banner
 * here: on a public page the featured event is simply the FIRST CARD IN THE GRID
 * carrying the badge. When the queue resolves to nothing, the first card is an
 * ordinary event card and the section is otherwise unchanged.
 *
 * Nothing is computed client-side. The backend returns the section already
 * capped and already ordered, and separately returns which event is featured;
 * this component neither sorts, filters, slices, nor picks.
 */
export function PublicEventsSection({ eyebrow, title, intro }: PublicEventsSectionProps = {}) {
  const { data: events, isPending, isError } = usePublicEvents();
  const { data: featured } = useFeaturedEvent();

  return (
    <section aria-labelledby="public-events-heading" className="w-full">
      {/* 1312px, the ui-guidelines §15 max content width `PublicHeader` also uses. */}
      <div className="mx-auto flex w-full max-w-328 flex-col gap-6 px-4 py-16">
        <div className="flex flex-col gap-2">
          {eyebrow && <p className="text-p6 font-semibold text-dkgreen uppercase">{eyebrow}</p>}
          <h2 id="public-events-heading" className="text-h2 text-text-primary">
            {title ?? EVENTS_STRINGS.PUBLIC_SECTION_TITLE}
          </h2>
          {/* No max-width here, deliberately - the client's design review flagged
            this paragraph as too narrow (constrained to ~2/3 of the column) and
            asked for it to fill the available width instead. */}
          <p className="text-p4 text-text-secondary">
            {intro ?? EVENTS_STRINGS.PUBLIC_SECTION_SUBTITLE}
          </p>
        </div>
        <PublicEventsBody
          events={events}
          isPending={isPending}
          isError={isError}
          featuredTitle={featured?.title}
        />
      </div>
    </section>
  );
}

interface PublicEventsBodyProps {
  events: PublicEvent[] | undefined;
  isPending: boolean;
  isError: boolean;
  /**
   * The resolved featured event's title, or `undefined` when the queue is empty.
   * A title rather than an id because `GET /v1/events/featured` returns a
   * `FeaturedEventDto`, which the contract does not declare an id on - so the
   * two responses are correlated on the only field they share.
   */
  featuredTitle: string | undefined;
}

/**
 * Split out so the section's own heading always renders while the grid resolves:
 * a public marketing page must not reflow its whole section between states.
 */
function PublicEventsBody({ events, isPending, isError, featuredTitle }: PublicEventsBodyProps) {
  if (isPending) {
    return (
      <div className="flex justify-center py-16">
        <LoadingSpinner size="lg" label={EVENTS_STRINGS.LOADING} />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState
        title={EVENTS_STRINGS.LOAD_ERROR_TITLE}
        description={EVENTS_STRINGS.LOAD_ERROR_BODY}
      />
    );
  }

  if (!events || events.length === 0) {
    return (
      <EmptyState
        title={EVENTS_STRINGS.PUBLIC_SECTION_EMPTY_TITLE}
        description={EVENTS_STRINGS.PUBLIC_SECTION_EMPTY_BODY}
      />
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {events.map((event, index) => (
        <EventCard
          key={event.id}
          event={event}
          // Only the FIRST card can carry the badge, and only when the backend
          // resolved a featured event that is actually in this section. Matching
          // on any index would let the badge land mid-grid; matching on nothing
          // would put it on a card that is not the featured one.
          featured={index === 0 && featuredTitle !== undefined && event.title === featuredTitle}
        />
      ))}
    </div>
  );
}
